import React, { useRef, useContext, createContext, useMemo } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useT } from "@hooks";

import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout";
import ApplicationHeader from "@rio-cloud/rio-uikit/ApplicationHeader";

import SubNavigation from "@rio-cloud/rio-uikit/SubNavigation";
import { parseRoute } from "./routeHelper";
import { useRouteState } from "./routeHooks";
import { DefaultUserMenu } from "@rio-cloud/rio-user-menu-component";
import IframeResizer from "iframe-resizer-react";
import { config } from "../../config";
import { ApplicationLayoutBodyNavigation } from "@rio-cloud/rio-uikit";
import CustomAssetTree from "./MenuTree";
import { CoreContext } from "../../providers/CoreProvider";
import UnauthorizedScreen from "../../screens/Unauthorized";
import ThemeToggle from "./ThemeToggle";

const RouteUpdater = () => {
  const { search } = useLocation();
  useRouteState(() => {
    parseRoute(search);
  });

  return null;
};

// Componentes específicos para o ROUTING
const getRoutingMapItems = (t: (id: string) => string) => [
  {
    key: "map",
    route: <NavLink to="/routing/map/trips">{t("nav.routing.trips")}</NavLink>,
  },
  {
    key: "map",
    route: <NavLink to="/routing/map/fleets">{t("nav.routing.fleets")}</NavLink>,
  },
];

const RoutingHeader = () => {
  const t = useT();
  const userMenuItem = (
    <DefaultUserMenu environment={process.env.NODE_ENV === "development" ? "development" : "production"} />
  );

  const navItems = [
    { key: "dashboard", route: <NavLink to="/trucker-pay-gestor/dashboard">{t("nav.dashboard")}</NavLink> },
    { key: "despesas", route: <NavLink to="/trucker-pay-gestor/expenses">{t("nav.expenses")}</NavLink> },
    { key: "receitas", route: <NavLink to="/trucker-pay-gestor/recipes">{t("nav.recipes")}</NavLink> },
    { key: "abastecimentos", route: <NavLink to="/trucker-pay-gestor/fueling">{t("nav.fueling")}</NavLink> },
    { key: "pedagios", route: <NavLink to="/trucker-pay-gestor/tolls">{t("nav.tolls")}</NavLink> },
    { key: "manutencoes", route: <NavLink to="/trucker-pay-gestor/maintenance">{t("nav.maintenance")}</NavLink> },
    { key: "settings", route: <NavLink to="/trucker-pay-gestor/settings">{t("nav.settings")}</NavLink> },
  ];

  return (
    <ApplicationHeader
      appNavigator={<IframeResizer className="iFrameResizer" src={config.backend.MENU_SERVICE} />}
      homeRoute={<Link to={config.homeRoute || ""} />}
      appMenuItems={getRoutingMapItems(t)}
      label="Trucker|Pay Gestor"
      navItems={navItems}
      actionBarItems={[<ThemeToggle key="theme-toggle" />, userMenuItem]}
    />
  );
};

const RoutingMenuSubItems = () => {
  const t = useT();
  const items = {
    map: getRoutingMapItems(t),
  };

  const location = useLocation();

  return (
    <ApplicationLayoutBodyNavigation className={"margin-0 padding-0"}>
      <SubNavigation
        className="padding-0 margin-0"
        navItems={location?.pathname.includes("/routing/map") ? items.map : []}
      />
    </ApplicationLayoutBodyNavigation>
  );
};

type AppContextType = {
  sidebarRef: React.RefObject<HTMLDivElement | null> | null;
};

const AppContext = createContext<AppContextType>({
  sidebarRef: null,
});

const RoutingLayout = () => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { isUnauthorized } = useContext(CoreContext);

  const appContextValue = useMemo(() => ({ sidebarRef }), [sidebarRef]);

  return (
    <AppContext.Provider value={appContextValue}>
      <ApplicationLayout data-testid="app-layout">
        <ApplicationLayout.Header>
          <RoutingHeader />
        </ApplicationLayout.Header>

        <ApplicationLayout.Sidebar className="left">
          <CustomAssetTree />
        </ApplicationLayout.Sidebar>

        <ApplicationLayout.Sidebar className="right">
          <div id="app-right-sidebar-slot" />
        </ApplicationLayout.Sidebar>

        <ApplicationLayout.Body
          innerClassName="padding-0"
          className="padding-0 margin-0"
          navigation={!isUnauthorized ? <RoutingMenuSubItems /> : undefined}
        >
          {isUnauthorized ? <UnauthorizedScreen /> : <Outlet />}
          <RouteUpdater />
        </ApplicationLayout.Body>
      </ApplicationLayout>
    </AppContext.Provider>
  );
};

export default RoutingLayout;
